import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Empty } from "../components/UI";
import { Star, MessageSquareHeart } from "lucide-react";
import { toast } from "sonner";

export default function FeedbackSurveys() {
  useEffect(() => { document.title = "Feedback & Surveys | COCOA EHR"; }, []);
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [surveys, setSurveys] = useState({ surveys: [], averages: {} });

  const load = () => {
    api.get("/feedback").then((r) => setFeedback(r.data)).catch(() => {});
    api.get("/visit-surveys").then((r) => setSurveys(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const markReviewed = async (f) => {
    try { await api.put(`/feedback/${f.id}`, {}); toast.success("Marked reviewed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const openClient = (clientId) => clientId && navigate(`/clients/${clientId}/console`);

  return (
    <div data-testid="feedback-surveys-page">
      <PageHeader title="Feedback & Surveys" subtitle="Client-reported experience from the portal - click a name to open the client's chart" />
      <div className="mb-5 grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-2xl">
        {[["Overall", surveys.averages.overall, "/5"], ["Provider", surveys.averages.provider, "/5"],
          ["Facility", surveys.averages.facility, "/5"], ["NPS", surveys.averages.nps, "/10"],
          ["Responses", surveys.averages.count, ""]].map(([l, v, s]) => (
          <Card key={l} className="p-3.5 text-center">
            <div className="font-heading text-xl font-bold text-[#3E2723]">{v ?? 0}<span className="text-xs text-[#84675B]">{s}</span></div>
            <div className="text-[10px] font-semibold text-[#7B6157]">{l}</div>
          </Card>
        ))}
      </div>
      <div className="grid gap-5 lg:grid-cols-2 items-start">
        <Card className="p-5" data-testid="feedback-panel">
          <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723] mb-3">
            <MessageSquareHeart size={15} className="text-[#7B6157]" /> Client Feedback
            {feedback.filter((f) => f.status === "new").length > 0 && <span className="rounded-full bg-[#FFB300] px-2 py-0.5 text-[10px] font-bold text-[#3E2723]">{feedback.filter((f) => f.status === "new").length} new</span>}
          </h3>
          {feedback.length === 0 ? <Empty text="No feedback yet" /> : (
            <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
              {feedback.map((f) => (
                <div key={f.id} className="rounded-xl border border-[#F1EEE7] px-4 py-3" data-testid={`feedback-${f.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <button onClick={() => openClient(f.client_id)} className="font-bold text-[#3E2723] hover:underline" data-testid={`feedback-client-${f.id}`}>{f.client_name}</button>
                    <span className="rounded-full bg-[#F5F2EB] px-2 py-0.5 text-[10px] font-bold text-[#6D4C41]">{f.category}</span>
                    {f.rating > 0 && <span className="flex items-center gap-0.5 text-[11px] font-bold text-[#8D5A00]">{f.rating}<Star size={11} className="fill-[#FFB300] text-[#FFB300]" /></span>}
                    <span className="ml-auto text-[10px] text-[#84675B]">{dayjs(f.created_at).format("MMM D")}</span>
                  </div>
                  <div className="mt-1 text-xs text-[#6D4C41]">{f.message}</div>
                  {f.status === "new" ? (
                    <button data-testid={`feedback-review-${f.id}`} onClick={() => markReviewed(f)}
                      className="mt-2 rounded-full bg-[#E8F5E9] px-3 py-1 text-[11px] font-bold text-[#2E7D32] hover:bg-[#C8E6C9]">Mark Reviewed</button>
                  ) : <div className="mt-1 text-[10px] text-[#84675B]">Reviewed by {f.reviewed_by}</div>}
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-5" data-testid="surveys-panel">
          <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723] mb-3"><Star size={15} className="text-[#7B6157]" /> Visit Surveys</h3>
          {(surveys.surveys || []).length === 0 ? <Empty text="No surveys submitted yet" /> : (
            <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
              {surveys.surveys.map((s) => (
                <div key={s.id} className="rounded-xl border border-[#F1EEE7] px-4 py-3" data-testid={`survey-${s.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <button onClick={() => openClient(s.client_id)} className="font-bold text-[#3E2723] hover:underline" data-testid={`survey-client-${s.id}`}>{s.client_name}</button>
                    <span className="text-xs text-[#7B6157]">{s.appointment_type} · {dayjs(s.appointment_date).format("MMM D")}</span>
                    <span className={`ml-auto flex items-center gap-0.5 font-bold ${s.overall <= 2 ? "text-[#C62828]" : "text-[#8D5A00]"}`}>{s.overall}<Star size={12} className="fill-[#FFB300] text-[#FFB300]" /></span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-[#84675B]">Provider {s.provider_rating}/5 · Facility {s.facility_rating}/5 · Recommend {s.recommend}/10</div>
                  {s.comments && <div className="mt-1 text-xs text-[#6D4C41]">"{s.comments}"</div>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
